import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Divider,
  TextField,
  Autocomplete,
} from '@mui/material';
import StyleIcon from '@mui/icons-material/Style';
import {
  COLOR_PALETTE_VALUES,
  SEASONALITY_VALUES,
  type ColorPalette,
  type Seasonality,
} from '../../contracts/openapi/assetContracts';
import {
  colorPaletteLabels,
  seasonalityLabels,
} from '../../contracts/openapi/assetEnumLabels';
import { environmentSafeLog } from '../../utils/environment';
import { LooksLayerMetadata } from '../../types/referencelayerMetadata';
import { API_STRING_MAX_LENGTH, LOOKS_METADATA } from '../../constants/apiMetadataConstraints';
import {
  buildEnumOptions,
  coerceEnumValue,
} from './enumFieldUtils';

interface LooksMetadataEditorProps {
  layerMetadata: LooksLayerMetadata;
  isEditable?: boolean;
  onMetadataChange?: (updatedMetadata: LooksLayerMetadata) => void;
  // Legacy support during migration
  asset?: any;
  // AI-generated description to auto-populate Look Description field
  aiGeneratedDescription?: string;
}

/**
 * Apply intelligent defaults for Looks layer to prevent null/undefined/empty values
 * This ensures all fields have sensible defaults to avoid backend validation errors
 * Similar to Stars layer implementation - provides smart fallbacks
 * when AI doesn't detect values or returns null/empty fields
 */
export const applyIntelligentLooksDefaults = (data: any) => {
  // Start with existing data, then apply defaults for any null/undefined values
  const defaults = {
    // Core look information - mandatory fields must have non-empty defaults
    outfitName: 'Unknown Outfit',
    style: 'Casual', // Mandatory field - must have default
    styleCategory: 'Casual',
    occasion: 'Casual',

    // Visual characteristics - mandatory fields must have non-empty defaults
    colorScheme: 'neutral',
    colorPalette: 'NEUTRAL',
    primaryGarment: 'Unknown', // Mandatory field - must have default
    primaryColors: ['Unknown'], // Mandatory field - backend expects List<String>, @NotEmpty
    patterns: [],
    materials: [],

    // Garments & Accessories
    accessories: [],
    jewelryType: '', // Backend expects String, not array

    // Context & Occasion
    seasonality: 'All-Season',
    timeOfDay: 'All-Day',
    formality: 'Casual', // Mandatory field
    priceRange: '',

    // Energy & Style
    energy: 'medium',

    // Brand information
    brandNames: [], // Backend expects List<String>, max 10 items

    // V2.0 compatibility
    layerType: 'looks',
  };

  // Apply defaults only for null, undefined values (NOT empty strings)
  const result = { ...data };

  // Apply defaults for null/undefined fields
  Object.keys(defaults).forEach(key => {
    if (result[key] === null || result[key] === undefined) {
      const defaultValue = defaults[key as keyof typeof defaults];
      if (Array.isArray(defaultValue)) {
        result[key] = [];
      } else {
        result[key] = defaultValue;
      }
    }
  });

  // Ensure style and styleCategory are strings (backend expects String, not array)
  if (Array.isArray(result.style)) {
    result.style = (result.style[0] ?? result.style.join(', ') ?? '').trim() || 'Casual';
  } else if (result.style === null || result.style === undefined || (typeof result.style === 'string' && result.style.trim() === '')) {
    result.style = result.style || 'Casual';
  }
  if (Array.isArray(result.styleCategory)) {
    result.styleCategory = (result.styleCategory[0] ?? result.styleCategory.join(', ') ?? '').trim() || 'Casual';
  } else if (result.styleCategory === null || result.styleCategory === undefined || (typeof result.styleCategory === 'string' && result.styleCategory.trim() === '')) {
    result.styleCategory = result.styleCategory || 'Casual';
  }

  // Ensure primaryColors is an array (backend expects List<String>, @NotEmpty)
  // Convert string to array if needed for backward compatibility
  if (!Array.isArray(result.primaryColors)) {
    if (typeof result.primaryColors === 'string' && result.primaryColors.trim() !== '') {
      result.primaryColors = result.primaryColors.split(',').map((c: string) => c.trim()).filter((c: string) => c.length > 0);
    } else {
      result.primaryColors = ['Unknown']; // @NotEmpty requirement
    }
  } else if (result.primaryColors.length === 0) {
    result.primaryColors = ['Unknown']; // @NotEmpty requirement
  }
  if (!Array.isArray(result.patterns)) {
    result.patterns = [];
  }
  if (!Array.isArray(result.materials)) {
    result.materials = [];
  }
  if (!Array.isArray(result.accessories)) {
    result.accessories = [];
  }
  
  // Ensure jewelryType is a string (backend expects String, not array)
  // Convert array to comma-separated string if it's an array
  if (Array.isArray(result.jewelryType)) {
    result.jewelryType = result.jewelryType.length > 0 ? result.jewelryType.join(', ') : '';
  } else if (!result.jewelryType || (typeof result.jewelryType === 'string' && result.jewelryType.trim() === '')) {
    result.jewelryType = '';
  }
  
  // Ensure brandNames is an array (backend expects List<String>, max 10 items)
  // Convert string to array if needed for backward compatibility
  if (!Array.isArray(result.brandNames)) {
    if (typeof result.brandNames === 'string' && result.brandNames.trim() !== '') {
      result.brandNames = result.brandNames.split(',').map((b: string) => b.trim()).filter((b: string) => b.length > 0);
    } else {
      result.brandNames = [];
    }
  }

  return result;
};

const LooksMetadataEditor: React.FC<LooksMetadataEditorProps> = ({
  layerMetadata,
  asset,
  isEditable = false,
  onMetadataChange,
  aiGeneratedDescription,
}) => {
  // V2.0 ARCHITECTURE: Use layerMetadata first, with legacy fallback
  const looksMetadata = React.useMemo(() => {
    // Priority 1: V2.0 layerMetadata prop (preferred)
    if (layerMetadata) {
      environmentSafeLog('📌 [LOOKS V2.0] Using layerMetadata prop');
      // Apply intelligent defaults even when layerMetadata is provided
      const processedMetadata = applyIntelligentLooksDefaults(layerMetadata);
      return processedMetadata;
    }

    // Legacy fallback for backward compatibility
    console.log('🚨 [LOOKS AI CONTRACT] Full asset object received:', asset);

    environmentSafeLog(
      '🔎 [LOOKS AI CONTRACT] Extracting looksMetadata from asset:',
      {
        hasLooksMetadata: !!asset?.looksMetadata,
        hasAiContractMetadata: !!asset?.aiMetadata?.layerMetadata,
        hasAiLooksMetadata: !!asset?.aiMetadata?.looksMetadata,
        hasMetadataLooksMetadata: !!asset?.metadata?.looksMetadata,
      }
    );

    let rawMetadata = null;

    // Priority 1: AI Contract layerMetadata (from AI Response Contract)
    if (asset?.aiMetadata?.layerMetadata && asset.layer === 'L') {
      environmentSafeLog(
        '📌 [LOOKS AI CONTRACT] Using aiMetadata.layerMetadata from AI Response Contract'
      );
      rawMetadata = asset.aiMetadata.layerMetadata;
    }
    // Priority 2: Root level looksMetadata (legacy AI-generated)
    else if (asset.looksMetadata) {
      environmentSafeLog(
        '📌 [LOOKS AI CONTRACT] Using root looksMetadata (legacy)'
      );
      rawMetadata = asset.looksMetadata;
    }
    // Priority 3: AI metadata structure (nested legacy)
    else if (asset.aiMetadata?.looksMetadata) {
      environmentSafeLog(
        '📌 [LOOKS AI CONTRACT] Using aiMetadata.looksMetadata (legacy)'
      );
      rawMetadata = asset.aiMetadata.looksMetadata;
    }
    // Priority 4: Legacy metadata structure
    else if (asset.metadata?.looksMetadata) {
      environmentSafeLog(
        '📌 [LOOKS AI CONTRACT] Using metadata.looksMetadata (legacy)'
      );
      rawMetadata = asset.metadata.looksMetadata;
    } else {
      environmentSafeLog(
        '⚠️ [LOOKS AI CONTRACT] No looks metadata found, using empty object'
      );
      rawMetadata = {};
    }

    // Apply nested data access pattern for backward compatibility
    const actualLooksMetadata =
      rawMetadata?.looksMetadata ||
      rawMetadata?.data?.looksMetadata ||
      rawMetadata;
    environmentSafeLog(
      '🔄 [LOOKS AI CONTRACT] Final metadata after nested access:',
      actualLooksMetadata
    );

    // INTELLIGENT DEFAULTS: Apply smart defaults to handle null/missing values
    const processedMetadata = applyIntelligentLooksDefaults(actualLooksMetadata);

    environmentSafeLog(
      '👗 [LOOKS INTELLIGENT DEFAULTS] Applied smart defaults to Looks metadata',
      { original: actualLooksMetadata, processed: processedMetadata }
    );

    return processedMetadata;
  }, [layerMetadata, asset]);

  // State for editable fields - initialize with extracted metadata
  const [editableData, setEditableData] = React.useState(() => {
    console.log(
      '🚀 [LOOKS INFO INIT] Initializing editableData state with looksMetadata:',
      looksMetadata
    );

    // Auto-populate Look Description with AI-generated description if creatorDescription is empty
    // Check multiple possible description locations
    const initialDescription =
      looksMetadata?.creatorDescription ||
      looksMetadata?.description ||
      aiGeneratedDescription ||
      '';
    if (aiGeneratedDescription && !looksMetadata?.creatorDescription && !looksMetadata?.description) {
      console.log('📝 [LOOKS INFO] Auto-populating Look Description with AI-generated description:', aiGeneratedDescription);
    }

    return {
      // Creator Input (CRITICAL for AI) - Use AI description as fallback
      creatorDescription: initialDescription,

      // Core Style Information
      outfitName: looksMetadata?.outfitName || '',
      style: looksMetadata?.style || '',
      styleCategory: looksMetadata?.styleCategory || '',
      occasion: Array.isArray(looksMetadata?.occasion)
        ? (looksMetadata.occasion[0] ?? looksMetadata.occasion.join(', ') ?? '')
        : (typeof looksMetadata?.occasion === 'string' ? looksMetadata.occasion : ''),

      // Color & Visual Elements
      primaryColors: Array.isArray(looksMetadata?.primaryColors)
        ? looksMetadata?.primaryColors
        : typeof looksMetadata?.primaryColors === 'string' &&
          looksMetadata?.primaryColors
        ? looksMetadata.primaryColors.split(',').map((c: string) => c.trim())
        : [],
      colorPalette: coerceEnumValue(looksMetadata?.colorPalette, COLOR_PALETTE_VALUES),
      colorScheme: Array.isArray(looksMetadata?.colorScheme)
        ? (looksMetadata.colorScheme[0] ?? looksMetadata.colorScheme.join(', ') ?? '')
        : (typeof looksMetadata?.colorScheme === 'string' ? looksMetadata.colorScheme : ''),

      // Garments & Materials
      primaryGarment: looksMetadata?.primaryGarment || '',
      patterns: Array.isArray(looksMetadata?.patterns)
        ? looksMetadata?.patterns
        : typeof looksMetadata?.patterns === 'string' && looksMetadata?.patterns
        ? looksMetadata.patterns.split(',').map((p: string) => p.trim())
        : [],
      materials: Array.isArray(looksMetadata?.materials)
        ? looksMetadata?.materials
        : typeof looksMetadata?.materials === 'string' &&
          looksMetadata?.materials
        ? looksMetadata.materials.split(',').map((m: string) => m.trim())
        : [],

      // Context & Timing
      seasonality: coerceEnumValue(looksMetadata?.seasonality, SEASONALITY_VALUES),
      timeOfDay: looksMetadata?.timeOfDay || '',

      // Accessories & Details
      accessories: Array.isArray(looksMetadata?.accessories)
        ? looksMetadata?.accessories
        : typeof looksMetadata?.accessories === 'string' &&
          looksMetadata?.accessories
        ? looksMetadata.accessories.split(',').map((a: string) => a.trim())
        : [],
      // Handle jewelryType as string (convert array to comma-separated string if needed)
      jewelryType: Array.isArray(looksMetadata?.jewelryType)
        ? looksMetadata.jewelryType.join(', ')
        : looksMetadata?.jewelryType || '',

      // Brand & Pricing
      // Handle brandNames as array (backend expects List<String>)
      brandNames: Array.isArray(looksMetadata?.brandNames)
        ? looksMetadata.brandNames
        : typeof looksMetadata?.brandNames === 'string' && looksMetadata.brandNames.trim() !== ''
        ? looksMetadata.brandNames.split(',').map((b: string) => b.trim()).filter((b: string) => b.length > 0)
        : looksMetadata?.brandName
        ? [looksMetadata.brandName]
        : [],
      priceRange: looksMetadata?.priceRange || '',
    };
  });

  // Track metadata hash to prevent infinite update loops
  const metadataHashRef = useRef<string>('');
  const computedHash = JSON.stringify(looksMetadata);

  // 🔧 FIX: Use ref to track latest editableData for cleanup without causing re-renders
  const editableDataRef = useRef(editableData);
  useEffect(() => {
    editableDataRef.current = editableData;
  }, [editableData]);

  // Update editableData when looksMetadata changes (from AI generation)
  React.useEffect(() => {
    if (computedHash !== metadataHashRef.current) {
      console.log(
        '🔄 [LOOKS INFO] looksMetadata changed, updating editableData'
      );
      metadataHashRef.current = computedHash;

      // Auto-populate Look Description with AI-generated description if creatorDescription is empty
      // Check multiple possible description locations
      const updatedDescription =
        looksMetadata?.creatorDescription ||
        looksMetadata?.description ||
        aiGeneratedDescription ||
        '';
      if (aiGeneratedDescription && !looksMetadata?.creatorDescription && !looksMetadata?.description) {
        console.log('📝 [LOOKS INFO] Auto-populating Look Description with AI-generated description on update:', aiGeneratedDescription);
      }

      setEditableData({
        // Creator Input (CRITICAL for AI) - Use AI description as fallback
        creatorDescription: updatedDescription,

        // Core Style Information
        outfitName: looksMetadata?.outfitName || '',
        style: looksMetadata?.style || '',
        styleCategory: looksMetadata?.styleCategory || '',
        occasion: Array.isArray(looksMetadata?.occasion)
          ? (looksMetadata.occasion[0] ?? looksMetadata.occasion.join(', ') ?? '')
          : (typeof looksMetadata?.occasion === 'string' ? looksMetadata.occasion : ''),

        // Color & Visual Elements
        primaryColors: Array.isArray(looksMetadata?.primaryColors)
          ? looksMetadata?.primaryColors
          : typeof looksMetadata?.primaryColors === 'string' &&
            looksMetadata?.primaryColors
          ? looksMetadata.primaryColors.split(',').map((c: string) => c.trim())
          : [],
        colorPalette: coerceEnumValue(looksMetadata?.colorPalette, COLOR_PALETTE_VALUES),
        colorScheme: Array.isArray(looksMetadata?.colorScheme)
          ? (looksMetadata.colorScheme[0] ?? looksMetadata.colorScheme.join(', ') ?? '')
          : (typeof looksMetadata?.colorScheme === 'string' ? looksMetadata.colorScheme : ''),

        // Garments & Materials
        primaryGarment: looksMetadata?.primaryGarment || '',
        patterns: Array.isArray(looksMetadata?.patterns)
          ? looksMetadata?.patterns
          : typeof looksMetadata?.patterns === 'string' &&
            looksMetadata?.patterns
          ? looksMetadata.patterns.split(',').map((p: string) => p.trim())
          : [],
        materials: Array.isArray(looksMetadata?.materials)
          ? looksMetadata?.materials
          : typeof looksMetadata?.materials === 'string' &&
            looksMetadata?.materials
          ? looksMetadata.materials.split(',').map((m: string) => m.trim())
          : [],

        // Context & Timing
        seasonality: coerceEnumValue(looksMetadata?.seasonality, SEASONALITY_VALUES),
        timeOfDay: looksMetadata?.timeOfDay || '',

        // Accessories & Details
        accessories: Array.isArray(looksMetadata?.accessories)
          ? looksMetadata?.accessories
          : typeof looksMetadata?.accessories === 'string' &&
            looksMetadata?.accessories
          ? looksMetadata.accessories.split(',').map((a: string) => a.trim())
          : [],
        // Handle jewelryType as string (convert array to comma-separated string if needed)
        jewelryType: Array.isArray(looksMetadata?.jewelryType)
          ? looksMetadata.jewelryType.join(', ')
          : looksMetadata?.jewelryType || '',

        // Brand & Pricing
        // Handle brandNames as array (backend expects List<String>)
        brandNames: Array.isArray(looksMetadata?.brandNames)
          ? looksMetadata.brandNames
          : typeof looksMetadata?.brandNames === 'string' && looksMetadata.brandNames.trim() !== ''
          ? looksMetadata.brandNames.split(',').map((b: string) => b.trim()).filter((b: string) => b.length > 0)
          : looksMetadata?.brandName
          ? [looksMetadata.brandName]
          : [],
        priceRange: looksMetadata?.priceRange || '',
      });
    }
  }, [computedHash, looksMetadata, aiGeneratedDescription]);

  // Debounce timeouts for field changes
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Enhanced field change handler with debouncing
  const handleFieldChange = useCallback(
    (field: string, value: string | string[]) => {
      setEditableData(prev => ({ ...prev, [field]: value }));

      if (debounceTimeouts.current[field]) {
        clearTimeout(debounceTimeouts.current[field]);
      }

      debounceTimeouts.current[field] = setTimeout(() => {
        if (onMetadataChange) {
          setEditableData(current => {
            // Process jewelryType: ensure it's a string (convert array if needed)
            let processedJewelryType = '';
            
            if (field === 'jewelryType') {
              // Use the value parameter directly for jewelryType field
              processedJewelryType = Array.isArray(value) ? value.join(', ') : (value || '');
            } else {
              // For other fields, use current state value
              processedJewelryType = Array.isArray(current.jewelryType) ? current.jewelryType.join(', ') : (current.jewelryType || '');
            }
            
            // Process brandNames: ensure it's an array (backend expects List<String>)
            let processedBrandNames: string[] = [];
            if (field === 'brandNames') {
              // Use the value parameter directly for brandNames field
              processedBrandNames = Array.isArray(value) ? value : (typeof value === 'string' && value.trim() !== '' ? [value] : []);
            } else {
              // For other fields, use current state value
              processedBrandNames = Array.isArray(current.brandNames) ? current.brandNames : (typeof current.brandNames === 'string' && current.brandNames.trim() !== '' ? [current.brandNames] : []);
            }
            
            // Process primaryColors: ensure it's an array; allow [] so validation can fail when required
            const toPrimaryColorsArray = (val: unknown): string[] => {
              if (Array.isArray(val)) return val;
              if (typeof val === 'string' && val.trim() !== '') return [val];
              return [];
            };
            let processedPrimaryColors: string[] = [];
            if (field === 'primaryColors') {
              processedPrimaryColors = toPrimaryColorsArray(value);
            } else {
              processedPrimaryColors = toPrimaryColorsArray(current.primaryColors);
            }

            // Normalize required string field: pass through current value, only coerce null/undefined to ''
            const toRequiredString = (val: unknown): string => {
              if (val === undefined || val === null) return '';
              if (typeof val === 'string') return val;
              if (Array.isArray(val)) return (val[0] ?? val.join(', ') ?? '').trim();
              return String(val).trim();
            };

            const processedData: LooksLayerMetadata = {
              ...current,
              secondaryColors: undefined,
              garments: undefined,
              layerType: 'looks',
              // Required fields: pass through value (allow ''), no default injection
              style: toRequiredString(current.style),
              outfitName: toRequiredString(current.outfitName),
              styleCategory: toRequiredString(current.styleCategory),
              occasion: toRequiredString(current.occasion),
              colorScheme: toRequiredString(current.colorScheme),
              colorPalette: coerceEnumValue(current.colorPalette, COLOR_PALETTE_VALUES),
              primaryGarment: toRequiredString(current.primaryGarment),
              primaryColors: processedPrimaryColors,
              // formality/energy are not in editable state; take from looksMetadata, allow empty for formality
              formality: toRequiredString(looksMetadata?.formality ?? ''),
              energy: looksMetadata?.energy ?? 'medium',
              patterns: current.patterns || [],
              materials: current.materials || [],
              seasonality: coerceEnumValue(current.seasonality, SEASONALITY_VALUES),
              timeOfDay: current.timeOfDay || 'any',
              accessories: current.accessories || [],
              jewelryType: processedJewelryType,
              brandNames: processedBrandNames,
              priceRange: current.priceRange || 'mid-range',
            };
            
            // Explicitly delete fields that shouldn't be in the metadata (not in API)
            delete processedData.secondaryColors;
            delete processedData.garments;
            delete (processedData as any).primaryColor;
            delete (processedData as any).aesthetic;
            onMetadataChange(processedData);
            console.log(`💾 [LOOKS METADATA] Debounced change for field "${field}":`, processedData[field as keyof LooksLayerMetadata]);
            return current;
          });
        }
      }, 100); // CRITICAL FIX: Reduced from 500ms to 100ms for faster propagation
    },
    [onMetadataChange]
  );

  // Cleanup timeouts on unmount AND flush pending changes
  // 🔧 FIX: Use empty dependency array to only run on unmount, use ref for latest data
  React.useEffect(() => {
    return () => {
      // CRITICAL FIX: Flush all pending debounced changes before unmount
      // This ensures user edits in Review & Submit step are not lost
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });

      // Immediately trigger onMetadataChange with latest editableData from ref
      if (onMetadataChange && editableDataRef.current) {
        const processedData: LooksLayerMetadata = {
          ...layerMetadata,
          ...editableDataRef.current,
          // Ensure required V2.0 fields
          layerType: 'looks',
          energy: layerMetadata?.energy || 'medium',
          formality: layerMetadata?.formality || 'casual',
          colorPalette: coerceEnumValue(editableDataRef.current.colorPalette, COLOR_PALETTE_VALUES),
        };
        onMetadataChange(processedData);
        console.log('💾 [LOOKS METADATA] Flushing pending changes on unmount:', {
          fieldsChanged: Object.keys(editableDataRef.current).filter(key =>
            editableDataRef.current[key as keyof typeof editableDataRef.current] !== layerMetadata?.[key as keyof typeof layerMetadata]
          ),
          fullData: processedData
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoized dropdown options to prevent unnecessary re-renders
  const styleOptions = useMemo(
    () => [
      'Casual',
      'Formal',
      'Business',
      'Streetwear',
      'Vintage',
      'Bohemian',
      'Minimalist',
      'Elegant',
      'Edgy',
      'Romantic',
      'Sporty',
      'Gothic',
      'Preppy',
      'Punk',
      'Chic',
    ],
    []
  );

  const styleCategoryOptions = useMemo(
    () => [
      'Everyday',
      'Evening',
      'Formal',
      'Casual',
      'Work',
      'Party',
      'Street',
      'Performance',
      'Red Carpet',
      'Editorial',
      'Avant-garde',
      'Traditional',
    ],
    []
  );

  const occasionOptions = useMemo(
    () => [
      'Daily',
      'Work',
      'Evening',
      'Party',
      'Formal',
      'Wedding',
      'Date',
      'Travel',
      'Performance',
      'Red Carpet',
      'Photoshoot',
      'Casual',
    ],
    []
  );

  const colorSchemeOptions = useMemo(
    () => [
      'Monochromatic',
      'Complementary',
      'Analogous',
      'Triadic',
      'Neutral',
      'Warm',
      'Cool',
      'Bold',
      'Pastel',
      'Earth Tones',
      'Vibrant',
      'Muted',
    ],
    []
  );

  const primaryColorsOptions = useMemo(
    () => [
      'Black',
      'White',
      'Gray',
      'Red',
      'Blue',
      'Green',
      'Yellow',
      'Purple',
      'Orange',
      'Pink',
      'Brown',
      'Gold',
      'Silver',
      'Beige',
      'Navy',
      'Maroon',
    ],
    []
  );

  const garmentOptions = useMemo(
    () => [
      'Dress',
      'Suit',
      'Blouse',
      'Shirt',
      'Pants',
      'Skirt',
      'Jacket',
      'Coat',
      'Sweater',
      'T-shirt',
      'Jeans',
      'Shorts',
      'Jumpsuit',
      'Top',
      'Bottom',
    ],
    []
  );

  const patternsOptions = useMemo(
    () => [
      'Solid',
      'Stripes',
      'Polka Dots',
      'Floral',
      'Geometric',
      'Abstract',
      'Plaid',
      'Leopard',
      'Zebra',
      'Paisley',
      'Check',
      'Argyle',
    ],
    []
  );

  const materialsOptions = useMemo(
    () => [
      'Cotton',
      'Silk',
      'Wool',
      'Linen',
      'Polyester',
      'Denim',
      'Leather',
      'Satin',
      'Velvet',
      'Chiffon',
      'Lace',
      'Cashmere',
      'Synthetic',
      'Mixed',
    ],
    []
  );

  const seasonalityOptions = useMemo(
    () => buildEnumOptions(SEASONALITY_VALUES, seasonalityLabels),
    []
  );

  const colorPaletteOptions = useMemo(
    () => buildEnumOptions(COLOR_PALETTE_VALUES, colorPaletteLabels),
    []
  );

  const timeOfDayOptions = useMemo(
    () => [
      'Morning',
      'Afternoon',
      'Evening',
      'Night',
      'All Day',
      'Specific Time',
    ],
    []
  );

  const accessoriesOptions = useMemo(
    () => [
      'Jewelry',
      'Handbag',
      'Shoes',
      'Belt',
      'Hat',
      'Scarf',
      'Sunglasses',
      'Watch',
      'Necklace',
      'Earrings',
      'Bracelet',
      'Ring',
    ],
    []
  );

  const jewelryTypeOptions = useMemo(
    () => [
      'None',
      'Minimal',
      'Statement',
      'Classic',
      'Modern',
      'Vintage',
      'Bohemian',
      'Elegant',
      'Bold',
      'Delicate',
    ],
    []
  );

  const brandNamesOptions = useMemo(
    () => [
      'Nike',
      'Adidas',
      'Gucci',
      'Prada',
      'Louis Vuitton',
      'Chanel',
      'Versace',
      'Dior',
      'Burberry',
      'Armani',
      'Calvin Klein',
      'Ralph Lauren',
      'Tommy Hilfiger',
      'Zara',
      'H&M',
      'Uniqlo',
      'Forever 21',
      'Gap',
      'Levi\'s',
      'Other',
    ],
    []
  );

  const priceRangeOptions = useMemo(
    () => [
      'Budget',
      'Affordable',
      'Mid-range',
      'High-end',
      'Luxury',
      'Designer',
      'Custom',
    ],
    []
  );

  // Don't render if no metadata available
  if (
    !looksMetadata &&
    !asset.looksMetadata &&
    !asset.aiMetadata?.looksMetadata
  ) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <StyleIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Looks Metadata
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Core Style Information */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Core Style Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              required
              fullWidth
              label="Outfit Name"
              value={editableData.outfitName}
              onChange={e => handleFieldChange('outfitName', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
              error={!editableData.outfitName || editableData.outfitName.trim() === ''}
              helperText={(!editableData.outfitName || editableData.outfitName.trim() === '' ? 'Outfit name is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
            />
          ) : (
            <Typography variant="body2">
              <strong>Outfit Name:</strong>{' '}
              {editableData.outfitName || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={styleOptions}
              value={typeof editableData.style === 'string' ? editableData.style : (editableData.style != null ? String(editableData.style) : '')}
              inputValue={typeof editableData.style === 'string' ? editableData.style : (editableData.style != null ? String(editableData.style) : '')}
              onInputChange={(event, newValue) =>
                handleFieldChange('style', typeof newValue === 'string' ? newValue : (newValue != null ? String(newValue) : ''))
              }
              renderInput={params => {
                const styleStr = typeof editableData.style === 'string' ? editableData.style : (editableData.style != null ? String(editableData.style) : '');
                const styleEmpty = !styleStr || styleStr.trim() === '';
                return (
                <TextField
                  {...params}
                  label="Style"
                  required
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  error={styleEmpty}
                  helperText={(styleEmpty ? 'Style is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
                />
              );}}
            />
          ) : (
            <Typography variant="body2">
              <strong>Style:</strong> {(typeof editableData.style === 'string' ? editableData.style : editableData.style != null ? String(editableData.style) : '') || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={styleCategoryOptions}
              value={typeof editableData.styleCategory === 'string' ? editableData.styleCategory : (editableData.styleCategory != null ? String(editableData.styleCategory) : '')}
              inputValue={typeof editableData.styleCategory === 'string' ? editableData.styleCategory : (editableData.styleCategory != null ? String(editableData.styleCategory) : '')}
              onInputChange={(event, newValue) =>
                handleFieldChange('styleCategory', typeof newValue === 'string' ? newValue : (newValue != null ? String(newValue) : ''))
              }
              renderInput={params => {
                const styleCategoryStr = typeof editableData.styleCategory === 'string' ? editableData.styleCategory : (editableData.styleCategory != null ? String(editableData.styleCategory) : '');
                const styleCategoryEmpty = !styleCategoryStr || styleCategoryStr.trim() === '';
                return (
                <TextField
                  {...params}
                  label="Style Category"
                  required
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  error={styleCategoryEmpty}
                  helperText={(styleCategoryEmpty ? 'Style category is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
                />
              );}}
            />
          ) : (
            <Typography variant="body2">
              <strong>Style Category:</strong>{' '}
              {(typeof editableData.styleCategory === 'string' ? editableData.styleCategory : editableData.styleCategory != null ? String(editableData.styleCategory) : '') || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={occasionOptions}
              value={typeof editableData.occasion === 'string' ? editableData.occasion : (Array.isArray(editableData.occasion) ? (editableData.occasion[0] ?? editableData.occasion.join(', ')) : '')}
              inputValue={typeof editableData.occasion === 'string' ? editableData.occasion : (Array.isArray(editableData.occasion) ? (editableData.occasion[0] ?? editableData.occasion.join(', ')) : '')}
              onInputChange={(event, newValue) =>
                handleFieldChange('occasion', newValue || '')
              }
              renderInput={params => {
                const occasionStr = typeof editableData.occasion === 'string' ? editableData.occasion : (Array.isArray(editableData.occasion) ? (editableData.occasion[0] ?? editableData.occasion.join(', ')) : '');
                return (
                <TextField 
                  {...params} 
                  label="Occasion" 
                  required
                  size="small"
                  error={!occasionStr || occasionStr.trim() === ''}
                  helperText={!occasionStr || occasionStr.trim() === '' ? 'Occasion is required' : ''}
                />
              );}}
            />
          ) : (
            <Typography variant="body2">
              <strong>Occasion:</strong>{' '}
              {typeof editableData.occasion === 'string' ? editableData.occasion : (Array.isArray(editableData.occasion) ? editableData.occasion.join(', ') : editableData.occasion) || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Color & Visual Elements */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Color & Visual Elements
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={primaryColorsOptions}
              value={(Array.isArray(editableData.primaryColors) ? editableData.primaryColors : []).slice(0, LOOKS_METADATA.primaryColors.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('primaryColors', (newValue ?? []).slice(0, LOOKS_METADATA.primaryColors.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Primary Colors"
                  required
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  error={!Array.isArray(editableData.primaryColors) || editableData.primaryColors.length === 0}
                  helperText={
                    !Array.isArray(editableData.primaryColors) || editableData.primaryColors.length === 0
                      ? 'Primary colors is required (1–10 items)'
                      : `1–${LOOKS_METADATA.primaryColors.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`
                  }
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Primary Colors:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.primaryColors || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.primaryColors?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              options={colorPaletteOptions}
              value={colorPaletteOptions.find(option => option.value === editableData.colorPalette) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('colorPalette', newValue?.value || '')
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Color Palette"
                  size="small"
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Color Palette:</strong>{' '}
              {editableData.colorPalette
                ? colorPaletteLabels[editableData.colorPalette as ColorPalette]
                : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={colorSchemeOptions}
              value={typeof editableData.colorScheme === 'string' ? editableData.colorScheme : (Array.isArray(editableData.colorScheme) ? (editableData.colorScheme[0] ?? editableData.colorScheme.join(', ')) : '')}
              inputValue={typeof editableData.colorScheme === 'string' ? editableData.colorScheme : (Array.isArray(editableData.colorScheme) ? (editableData.colorScheme[0] ?? editableData.colorScheme.join(', ')) : '')}
              onInputChange={(event, newValue) =>
                handleFieldChange('colorScheme', newValue || '')
              }
              renderInput={params => {
                const colorSchemeStr = typeof editableData.colorScheme === 'string' ? editableData.colorScheme : (Array.isArray(editableData.colorScheme) ? (editableData.colorScheme[0] ?? editableData.colorScheme.join(', ')) : '');
                return (
                <TextField 
                  {...params} 
                  label="Color Scheme" 
                  required
                  size="small"
                  error={!colorSchemeStr || colorSchemeStr.trim() === ''}
                  helperText={!colorSchemeStr || colorSchemeStr.trim() === '' ? 'Color scheme is required' : ''}
                />
              );}}
            />
          ) : (
            <Typography variant="body2">
              <strong>Color Scheme:</strong>{' '}
              {typeof editableData.colorScheme === 'string' ? editableData.colorScheme : (Array.isArray(editableData.colorScheme) ? editableData.colorScheme.join(', ') : editableData.colorScheme) || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Garments & Materials */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Garments & Materials
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={garmentOptions}
              value={editableData.primaryGarment}
              inputValue={editableData.primaryGarment}
              onInputChange={(event, newValue) =>
                handleFieldChange('primaryGarment', newValue || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Primary Garment" 
                  required
                  size="small"
                  error={!editableData.primaryGarment || editableData.primaryGarment.trim() === ''}
                  helperText={!editableData.primaryGarment || editableData.primaryGarment.trim() === '' ? 'Primary garment is required' : ''}
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Primary Garment:</strong>{' '}
              {editableData.primaryGarment || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={patternsOptions}
              value={(editableData.patterns || []).slice(0, LOOKS_METADATA.patterns.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('patterns', (newValue ?? []).slice(0, LOOKS_METADATA.patterns.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Patterns"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${LOOKS_METADATA.patterns.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Patterns:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.patterns || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.patterns?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={materialsOptions}
              value={(editableData.materials || []).slice(0, LOOKS_METADATA.materials.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('materials', (newValue ?? []).slice(0, LOOKS_METADATA.materials.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Materials"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${LOOKS_METADATA.materials.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Materials:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.materials || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.materials?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Context & Timing */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Context & Timing
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={seasonalityOptions}
              value={seasonalityOptions.find(option => option.value === editableData.seasonality) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('seasonality', newValue?.value || '')
              }
              renderInput={params => (
                <TextField {...params} label="Seasonality" size="small" />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Seasonality:</strong>{' '}
              {editableData.seasonality ? seasonalityLabels[editableData.seasonality as Seasonality] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={timeOfDayOptions}
              value={editableData.timeOfDay}
              inputValue={editableData.timeOfDay}
              onInputChange={(event, newValue) =>
                handleFieldChange('timeOfDay', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Time of Day" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Time of Day:</strong>{' '}
              {editableData.timeOfDay || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Accessories & Details */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Accessories & Details
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={accessoriesOptions}
              value={(editableData.accessories || []).slice(0, LOOKS_METADATA.accessories.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('accessories', (newValue ?? []).slice(0, LOOKS_METADATA.accessories.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Accessories"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${LOOKS_METADATA.accessories.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Accessories:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.accessories || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.accessories?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={jewelryTypeOptions}
              value={editableData.jewelryType}
              inputValue={editableData.jewelryType}
              onInputChange={(event, newValue) =>
                handleFieldChange('jewelryType', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Jewelry Type" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Jewelry Type:</strong>{' '}
              {editableData.jewelryType || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Brand & Pricing */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Brand & Pricing
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={brandNamesOptions}
              value={(Array.isArray(editableData.brandNames) ? editableData.brandNames : []).slice(0, LOOKS_METADATA.brandNames.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('brandNames', (newValue ?? []).slice(0, LOOKS_METADATA.brandNames.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Brand Names"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${LOOKS_METADATA.brandNames.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Brand Names:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(Array.isArray(editableData.brandNames) ? editableData.brandNames : []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.brandNames?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={priceRangeOptions}
              value={editableData.priceRange}
              inputValue={editableData.priceRange}
              onInputChange={(event, newValue) =>
                handleFieldChange('priceRange', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Price Range" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Price Range:</strong>{' '}
              {editableData.priceRange || 'Not specified'}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default LooksMetadataEditor;
